export interface ImageJobFieldMetadata {
  category: string;
  description: string;
  order: number;
}

const defineCategory = (
  category: string,
  fields: Array<[name: string, description: string]>,
  startOrder: number
): Record<string, ImageJobFieldMetadata> =>
  Object.fromEntries(
    fields.map(([name, description], index) => [
      name,
      { category, description, order: startOrder + index },
    ])
  );

export const IMAGE_JOB_FIELD_METADATA: Record<string, ImageJobFieldMetadata> = {
  ...defineCategory(
    '核心任务',
    [
      ['id', '图片生成任务的唯一 ID，用于查询任务、关联日志和排查问题。'],
      ['status', '任务当前状态，例如等待处理、处理中、已完成或失败。'],
      ['userId', '创建任务并承担积分消耗的用户 ID。'],
      ['prompt', '用户提交的原始提示词，是生成图片的主要文本输入。'],
      ['resultStorageKey', '生成结果在媒体存储中的规范键，用于解析和访问最终图片。'],
    ],
    0
  ),
  ...defineCategory(
    '生成参数',
    [
      ['model', '任务使用的站内模型 ID。'],
      ['mode', '生成模式，例如文生图、图生图或局部重绘。'],
      ['size', '请求的图片尺寸，通常以“宽x高”表示。'],
      ['quality', '请求的图片质量档位。'],
      ['source', '任务来源，例如网页、API、导演模式或 PPT。'],
      ['executionMode', '任务执行方式，区分同步执行与异步队列执行。'],
      ['channelId', '任务首选的 API 渠道 ID，用于定位实际调用渠道。'],
    ],
    100
  ),
  ...defineCategory(
    '输入与计费',
    [
      ['referenceImages', '参考图片列表，以 JSON 字符串保存媒体引用。'],
      ['maskImage', '局部重绘使用的蒙版图片媒体引用。'],
      ['creditsUsed', '本次生成任务计算并扣除的积分数量。'],
      ['createdAt', '任务创建时间。'],
      ['updatedAt', '任务最近一次状态或内容更新时间。'],
    ],
    200
  ),
  ...defineCategory(
    '错误与诊断',
    [
      ['userFacingErrorMessage', '经过清洗、可直接展示给普通用户的错误说明。'],
      ['errorMessage', '供管理员排查使用的内部错误详情，可能包含渠道响应信息。'],
      ['fallbackTrace', '渠道降级和重试轨迹，记录尝试链及最终接管结果。'],
    ],
    300
  ),
  ...defineCategory(
    '迭代与归属',
    [
      ['parentJobId', '上一次迭代任务的 ID，用于建立任务迭代关系。'],
      ['generation', '当前任务在迭代链中的代数，根任务通常为 1。'],
    ],
    400
  ),
  ...defineCategory(
    '导演业务',
    [
      ['directorProjectId', '导演模式中关联的项目 ID。'],
      ['directorShotId', '导演模式中关联的分镜 ID。'],
      ['directorCharacterId', '导演模式中关联的角色 ID。'],
      ['directorSceneId', '导演模式中关联的场景 ID。'],
      ['directorPropId', '导演模式中关联的道具 ID。'],
      ['usageRole', '图片在导演业务中的用途，例如角色图、场景图或分镜帧。'],
    ],
    500
  ),
  ...defineCategory(
    '作品管理',
    [
      ['title', '用户为作品设置的私有标题。'],
      ['description', '用户为作品设置的私有描述。'],
      ['isHidden', '是否在作品管理中软隐藏；隐藏不会删除数据库记录或媒体文件。'],
      ['isFavorite', '用户是否将该作品标记为收藏。'],
    ],
    600
  ),
  ...defineCategory(
    '公开与互动',
    [
      ['isPublic', '作品是否已公开到作品广场。'],
      ['publicTitle', '作品在公开展示时使用的标题。'],
      ['publicDescription', '作品在公开展示时使用的描述。'],
      ['publicCategory', '作品在广场中的公开分类。'],
      ['isPromptPublic', '公开作品时是否同时展示原始提示词。'],
      ['publishedAt', '作品最近一次公开发布的时间。'],
      ['viewCount', '作品在广场中的累计浏览次数。'],
      ['likeCount', '作品在广场中的点赞数量缓存。'],
      ['reportCount', '作品在广场中收到的举报数量。'],
    ],
    700
  ),
};

export function getImageJobFieldMetadata(name: string): ImageJobFieldMetadata | undefined {
  return IMAGE_JOB_FIELD_METADATA[name];
}

export function sortImageJobFields<T extends { name: string }>(fields: T[]): T[] {
  return fields
    .map((field, originalIndex) => ({ field, originalIndex }))
    .sort((left, right) => {
      const leftOrder = IMAGE_JOB_FIELD_METADATA[left.field.name]?.order ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = IMAGE_JOB_FIELD_METADATA[right.field.name]?.order ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.originalIndex - right.originalIndex;
    })
    .map(({ field }) => field);
}
